import type React from 'react';
import { useMemo } from 'react';
import { useAppSelector } from '../../hooks';
import { styled } from '@mui/material/styles';

//Redux

import { motion } from 'motion/react';
import {
  selectError,
  selectIsFreeFormIntentDetectionEnabled,
  selectIsBuilderPreview,
  selectLastWidgetComponent,
  selectWidgetMode,
  selectDismissableNotification,
  selectIsDelaying,
  selectVisibleWidgetComponents,
  selectAreLoadingDotsVisible,
  selectIsSubmitDisabledBySignInStep,
} from 'reducers/workflows';

// Components
import ScrollableMessagesContainer from '../ScrollableMessagesContainer';
import CustomerSatisfactionForm from 'components/CustomerSatisfactionForm/CustomerSatisfactionForm';
import AgathaMessage from 'components/AgathaMessage/AgathaMessage';

//Types
import type { WidgetComponent, ComponentTypes } from 'api/types';
import {
  isWorkflowTagSelectorComponent,
  isButtonStepWithHideFreeFormQueryField,
} from 'api/types';

//Effects
import { useHandleScrollBehavior } from 'components/App/hooks';
import useTracing from 'components/App/hooks/useTracing';
import { FreeFormInput } from 'components/FreeFormInput';
import { selectIsAutonomousAgent, selectIsDraft } from 'reducers/config';
import useScrollToBottomOnFreeFormQueryChange from 'components/App/hooks/useScrollToBottomOnFreeFormQueryChange';
import ErrorBanner from 'components/ErrorBanner';
import AutonomousAgentInput from 'components/AutonomousAgentInput';
import LiveChatInput from 'components/LiveChatInput/LiveChatInput';
import useAnnounceLoading from 'components/App/hooks/useAnnounceLoading';
import useFocusReceivedMessage from 'components/App/hooks/useFocusReceivedMessage';
import { useErrorBannerTrackingEvent } from 'components/App/hooks/useErrorBannerTrackingEvent';
import { useArtificialMessageDelay } from './useArtificialMessageDelay';
import { LoadingMessageWithDots } from 'components/LoadingMessageWithDots';
import {
  selectAreAgentTypingDotsVisible,
  selectLiveChatIntegration,
} from 'reducers/live-chat';
import { GenericStepComponent } from './GenericStepComponent';
import { Box } from '@mui/material';
import { DismissableNotificationToast } from 'components/DismissableNotificationToast';
import { LIVE_CHAT_BACKGROUND_IMAGE_CSS } from 'embed/constants';

const componentsWithNeverMindButton: ComponentTypes[] = [
  'form',
  'zendesk_attachment_upload',
];

const WorkflowsPage: React.FC<React.PropsWithChildren<unknown>> = () => {
  const error = useAppSelector(selectError);
  const isFreeFormIntentDetectionEnabled = useAppSelector(
    selectIsFreeFormIntentDetectionEnabled,
  );
  const isPreview = useAppSelector(selectIsBuilderPreview);
  const isDraft = useAppSelector(selectIsDraft);
  const lastWidgetComponent = useAppSelector(selectLastWidgetComponent);
  const widgetMode = useAppSelector(selectWidgetMode);
  const isAutonomousAgent = useAppSelector(selectIsAutonomousAgent);
  const dismissableNotification = useAppSelector(selectDismissableNotification);
  const isDelaying = useAppSelector(selectIsDelaying);
  const visibleWidgetComponents = useAppSelector(selectVisibleWidgetComponents);
  const areLoadingDotsVisible = useAppSelector(selectAreLoadingDotsVisible);
  const areAgentTypingDotsVisible = useAppSelector(
    selectAreAgentTypingDotsVisible,
  );
  const liveChatIntegration = useAppSelector(selectLiveChatIntegration);
  const isSubmitDisabledBySignInStep = useAppSelector(
    selectIsSubmitDisabledBySignInStep,
  );

  const lastComponentType = lastWidgetComponent?.component_type;

  // Effects
  useTracing();
  useScrollToBottomOnFreeFormQueryChange();
  useArtificialMessageDelay();
  useHandleScrollBehavior();
  useAnnounceLoading();
  useFocusReceivedMessage();
  useErrorBannerTrackingEvent();

  const isFreeFormInputVisible =
    isFreeFormIntentDetectionEnabled &&
    // Do not show free-form input if there is an error:
    !error &&
    // If preview mode, show free-form input only on landing page:
    (isPreview ? !isDraft : true) &&
    (areLoadingDotsVisible ||
      // components with "Never mind" button should not have free-form input unless they are sent:
      (!(
        !lastWidgetComponent?.isSent &&
        lastComponentType &&
        componentsWithNeverMindButton.includes(lastComponentType)
      ) &&
        // workflow tag selector should not have free-form input:
        !(
          lastWidgetComponent &&
          isWorkflowTagSelectorComponent(lastWidgetComponent)
        ))) &&
    // Do not show free-form input after end_interactive_email_chat:
    lastComponentType !== 'end_interactive_email_chat' &&
    !(
      lastWidgetComponent &&
      isButtonStepWithHideFreeFormQueryField(lastWidgetComponent)
    ) &&
    // Do not show free-form input during sign in step with disable_other_inptus
    !isSubmitDisabledBySignInStep;

  const getInputComponent = () => {
    if (widgetMode === 'liveChat') {
      if (
        (liveChatIntegration === 'kustomer' ||
          liveChatIntegration === 'sunco') &&
        (lastComponentType === 'buttons' || lastComponentType === 'form')
      ) {
        return null;
      }

      return <LiveChatInput />;
    }

    if (isFreeFormInputVisible && !isAutonomousAgent) {
      return <FreeFormInput isDelaying={isDelaying} />;
    }

    if (isAutonomousAgent) {
      return <AutonomousAgentInput />;
    }

    return null;
  };

  const animate = useMemo(
    () => ({
      y: '0px',
    }),
    [],
  );
  const initial = useMemo(
    () => ({
      y: '30px',
    }),
    [],
  );
  const transition = useMemo(
    () => ({
      bounce: 0.35,
      duration: 0.4,
      type: 'spring',
    }),
    [],
  );
  const sx = useMemo(() => ({ overflowY: 'hidden' }), []);

  return (
    <ContentContainer>
      <CustomerSatisfactionForm />
      <DismissableNotificationToast notification={dismissableNotification} />
      <ErrorBanner errorData={error} />
      <ScrollableMessagesContainer>
        {visibleWidgetComponents.map(
          (widgetComponent: WidgetComponent, index: number) => {
            return (
              <GenericStepComponent
                index={index}
                isLastStep={index === visibleWidgetComponents.length - 1}
                key={index}
                widgetComponent={widgetComponent}
              />
            );
          },
        )}
        {(areLoadingDotsVisible || areAgentTypingDotsVisible) && (
          <Box sx={sx}>
            <motion.div
              animate={animate}
              initial={initial}
              transition={transition}
            >
              <LoadingMessageWithDots />
            </motion.div>
          </Box>
        )}
      </ScrollableMessagesContainer>
      {/* render free form input or autonomous agent input */}
      {getInputComponent()}
    </ContentContainer>
  );
};

const ContentContainer = styled('div')`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;

  & > * {
    padding-left: 8px;
    padding-right: 8px;
  }
`;

export const AgathaLoadingMessage = styled(AgathaMessage)`
  background-color: ${props => props.theme.palette.colors.slate[100]};
  ${props => props.isLiveChat && LIVE_CHAT_BACKGROUND_IMAGE_CSS};
  min-height: 0;
  width: 61px;
  height: 35px;
  box-sizing: border-box;
  display: flex;
  justify-content: center;
  align-items: flex-start;
  left: 0px;
  border-radius: 25px;
`;

export default WorkflowsPage;
